// TODO: Colocated with custom-collections because custom-collections is the only consumer.
// Mirrors step-wrapper.tsx's inline `checkBeforeHooks` useEffect and the
// `beforeHooksLoaded` / `beforeHooksError` local state it drives. Keep both
// in sync — if step-wrapper starts reporting per-hook failure granularity
// or otherwise changes gate semantics, apply the same change here. If a
// future refactor shares lifecycle between step-wrapper and custom-collections,
// relocate this up to `src/hooks/` and have step-wrapper consume it.
import { useEffect, useState } from "react";
import {
  OnboardingApplication,
  StepAssets,
} from "@dreambigger/shared/src/types";
import { useApplication } from "../../../api";
import runStepHooks from "../utils/runStepHooks";

type ApplicationHelper = ReturnType<typeof useApplication>;

/**
 * Runs `assets.beforeHooks.hooks` once the application is available and
 * exposes the loaded/error state so the caller can gate rendering until
 * the hooks resolve. Behavior matches StepWrapper's inline implementation
 * (gate on load, show error UI when any hook rejects) so custom-collections picks
 * up the exact same lifecycle.
 */
export default function useBeforeHooks({
  assets,
  application,
  applicationHelper,
}: {
  assets: StepAssets;
  application?: OnboardingApplication;
  applicationHelper: ApplicationHelper;
}) {
  const [beforeHooksLoaded, setBeforeHooksLoaded] = useState(false);
  const [beforeHooksError, setBeforeHooksError] = useState(false);

  useEffect(() => {
    if (!application) return;

    const run = async () => {
      const hooks = assets?.beforeHooks?.hooks;

      if (Array.isArray(hooks) && hooks.length) {
        try {
          const results = await runStepHooks({
            hooks,
            applicationHelper,
            application,
          });

          let anyFailed = false;
          results.forEach((result, index) => {
            if (result.status === "rejected") {
              console.error(`Hook ${hooks[index].slug} rejected`);
              anyFailed = true;
            }
          });

          if (anyFailed) {
            setBeforeHooksError(true);
            return;
          }

          setBeforeHooksLoaded(true);
        } catch {
          setBeforeHooksError(true);
        }
      } else {
        setBeforeHooksLoaded(true);
      }
    };

    if (assets.beforeHooks) {
      run();
    }
  }, [application, assets.beforeHooks]);

  return { beforeHooksLoaded, beforeHooksError };
}
